/* Admin: Enquiries list + detail (contact form messages). */
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Link, navigate } from "../../lib/router-compat.jsx";
import AdminShell from "../../components/shell/AdminShell.jsx";
import { Button, Card, Badge, EmptyState, Skeleton, ConfirmDialog, Icon, showToast } from "../../ui/index.js";
import { useEnquiries } from "../../data/hooks.js";
import { updateEnquiryStatus } from "../../data/mutations.js";
import { deleteDoc, doc } from "firebase/firestore";
import { db } from "../../lib/firebase.js";
import { COL } from "../../data/collections.js";
import { formatRelative, formatDateTime } from "../../utils/format.js";

export function AdminEnquiriesListPage() {
  const { enquiries, loading } = useEnquiries();
  const [filter, setFilter] = useState("all");
  const filtered = enquiries.filter((e) => (filter === "all" ? true : e.status === filter));

  return (
    <AdminShell>
      <div className="admin-main__header">
        <div><h1 className="t-3xl">Enquiries</h1><p>Messages from the public contact form.</p></div>
      </div>
      <div className="chips" style={{ marginBottom: 24 }}>
        {[["all", "All"], ["new", "New"], ["replied", "Replied"]].map(([k, label]) => (
          <button key={k} className={`chip ${filter === k ? "active" : ""}`} onClick={() => setFilter(k)}>{label} ({k === "all" ? enquiries.length : enquiries.filter((e) => e.status === k).length})</button>
        ))}
      </div>
      {loading ? <Skeleton h={240} /> : filtered.length === 0 ? (
        <Card><EmptyState icon={<Icon.Inbox size={40} />} title="No enquiries yet." body="They'll appear here when visitors submit the contact form." /></Card>
      ) : (
        <table className="table">
          <thead><tr><th>Name</th><th>Email</th><th>Subject / Message</th><th style={{ width: 110 }}>Status</th><th style={{ width: 130 }}>Submitted</th><th className="table__actions">Actions</th></tr></thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.id} className="clickable" onClick={() => navigate(`/admin/enquiries/${e.id}`)}>
                <td style={{ fontWeight: 600 }}>{e.name}</td>
                <td className="t-sm muted">{e.email}</td>
                <td className="t-sm">
                  {e.subject && <div style={{ fontWeight: 500 }}>{e.subject}</div>}
                  <div className="muted" style={{ marginTop: 2, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", maxWidth: 460 }}>{(e.message || "").slice(0, 100)}{(e.message || "").length > 100 ? "…" : ""}</div>
                </td>
                <td><Badge variant={e.status === "new" ? "new" : "replied"}>{e.status === "new" ? "New" : "Replied"}</Badge></td>
                <td className="t-sm muted">{formatRelative(e.submittedAt)}</td>
                <td className="table__actions" onClick={(ev) => ev.stopPropagation()}><Button variant="ghost" size="sm" onClick={() => navigate(`/admin/enquiries/${e.id}`)}>View</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </AdminShell>
  );
}

export function AdminEnquiryDetailPage() {
  const { enquiryId } = useParams();
  const { enquiries, loading } = useEnquiries();
  const [showDelete, setShowDelete] = useState(false);

  if (loading) return <AdminShell><Skeleton h={300} /></AdminShell>;
  const enquiry = enquiries.find((e) => e.id === enquiryId);
  if (!enquiry) return <AdminShell><EmptyState title="Enquiry not found" /></AdminShell>;

  const toggleReplied = async () => {
    const next = enquiry.status === "new" ? "replied" : "new";
    try { await updateEnquiryStatus(enquiry.id, next); showToast({ intent: "success", title: next === "replied" ? "Marked as replied" : "Marked as new" }); }
    catch (err) { showToast({ intent: "danger", title: "Couldn't update", body: err.message }); }
  };
  const doDelete = async () => {
    try { await deleteDoc(doc(db, COL.enquiries, enquiry.id)); showToast({ intent: "success", title: "Enquiry deleted" }); navigate("/admin/enquiries"); }
    catch (err) { showToast({ intent: "danger", title: "Couldn't delete", body: err.message }); }
  };

  return (
    <AdminShell>
      <Link to="/admin/enquiries" className="back-link"><Icon.ArrowLeft size={14} />Back to enquiries</Link>
      <div className="row" style={{ marginTop: 16, marginBottom: 32, gap: 16 }}>
        <h1 className="t-3xl" style={{ margin: 0, flex: 1 }}>Enquiry from {enquiry.name}</h1>
        <Badge variant={enquiry.status === "new" ? "new" : "replied"}>{enquiry.status === "new" ? "New" : "Replied"}</Badge>
      </div>

      <div className="dashboard-split" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }}>
        <Card>
          <div className="stack-md">
            <div className="t-sm"><strong>From:</strong> {enquiry.name} &lt;{enquiry.email}&gt;</div>
            <div className="t-sm"><strong>Subject:</strong> {enquiry.subject || "(no subject)"}</div>
            <div className="t-sm"><strong>Submitted:</strong> {formatDateTime(enquiry.submittedAt)}</div>
          </div>
          <div style={{ marginTop: 32 }}>
            <div className="t-eyebrow" style={{ color: "var(--text-muted)", marginBottom: 12 }}>Message</div>
            <div className="t-base" style={{ background: "var(--bg-warm)", padding: 20, borderRadius: "var(--radius-md)", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{enquiry.message}</div>
          </div>
        </Card>
        <Card>
          <h3 className="t-base" style={{ margin: 0, marginBottom: 16, fontWeight: 600 }}>Actions</h3>
          <div className="stack-md" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <a href={`mailto:${enquiry.email}?subject=Re: ${encodeURIComponent(enquiry.subject || "Your enquiry")}`} className="btn btn--primary"><Icon.Send size={14} />Reply via email</a>
            <Button variant="secondary" onClick={toggleReplied}>{enquiry.status === "new" ? "Mark as replied" : "Mark as new"}</Button>
            <div style={{ borderTop: "1px solid var(--border)", marginTop: 8, paddingTop: 8 }}>
              <Button variant="ghost" onClick={() => setShowDelete(true)} style={{ color: "var(--danger)" }} className="btn--full"><Icon.Trash size={14} />Delete enquiry</Button>
            </div>
          </div>
        </Card>
      </div>

      <ConfirmDialog open={showDelete} title="Delete this enquiry?" body="It will be permanently removed." confirmLabel="Delete" destructive onConfirm={doDelete} onClose={() => setShowDelete(false)} />
    </AdminShell>
  );
}

export default AdminEnquiriesListPage;
